import { DAY_BOUNDARY_TIME_ZONE } from '@kiao-voice/core'
import type { BlogCategory } from '@kiao-voice/api-contract/blog'

/**
 * The zh-TW name for a category key, falling back to the KEY itself.
 *
 * The fallback is the point: labels arrive in the payload's envelope, so a key
 * whose vocabulary row was deleted (or a page cached before a category was
 * created) has no label to show. Printing `aitools` is legible and obviously a
 * gap; printing nothing silently deletes the category line from the article
 * header.
 *
 * A linear scan rather than a Map: the vocabulary is a handful of entries and
 * every call site has the array in hand already.
 */
export function blogCategoryLabel(categories: BlogCategory[], key: string): string {
  return categories.find((category) => category.key === key)?.label ?? key
}

/**
 * Blog dates render in Asia/Taipei wall time, everywhere and for everyone.
 *
 * Not the viewer's local zone: the same string is produced on the server and in
 * the browser (so hydration matches), it is the zone the editorial team writes
 * in, and a「更新於」that reads one day earlier for a reader in São Paulo would
 * disagree with the `dateModified` in the page's own JSON-LD.
 */
const TAIPEI_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: DAY_BOUNDARY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function taipeiYmd(iso: string): { year: string; month: string; day: string } | null {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null
  const parts = new Map(TAIPEI_PARTS.formatToParts(parsed).map((part) => [part.type, part.value]))
  const year = parts.get('year')
  const month = parts.get('month')
  const day = parts.get('day')
  if (!year || !month || !day) return null
  return { year, month, day }
}

/** `2026/08/15` — the list and「更新於」display form. Empty when unparsable. */
export function formatBlogDate(iso: string): string {
  const parts = taipeiYmd(iso)
  return parts ? `${parts.year}/${parts.month}/${parts.day}` : ''
}

/** `2026-08-15` — the `<time datetime>` and JSON-LD form. Empty when unparsable. */
export function blogDateAttribute(iso: string): string {
  const parts = taipeiYmd(iso)
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : ''
}

/** The four-digit Taipei year, or null when unparsable. */
export function blogYear(iso: string): string | null {
  return taipeiYmd(iso)?.year ?? null
}

/**
 * Whether the「YYYY 更新」pill belongs on this article.
 *
 * Green means a state that is true, not a badge an editor may award itself: the
 * article is current only while its last edit is in the year the reader is in.
 */
export function showsUpdatedThisYearPill(updatedAt: string, now: Date = new Date()): boolean {
  const year = blogYear(updatedAt)
  return year !== null && year === blogYear(now.toISOString())
}
